import { useState } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import DekontDialog from '@/components/DekontDialog';
import { EkSecici, kayittanSonraYukle } from '@/components/HareketEkleri';
import { useRef, useEffect, useMemo } from 'react';
import { trNorm } from '@/lib/arama';

const fmtTR = (n, d = 2) =>
  n == null ? '—' : Number(n).toLocaleString('tr-TR', { minimumFractionDigits: d, maximumFractionDigits: d });

const CURRENCIES = ['TRY', 'USD', 'EUR', 'GBP'];


const TX_TYPES = [
  { key: 'transfer', label: 'Hesaplar Arası Transfer' },
  { key: 'cari',     label: 'Ödeme' },
  { key: 'fx',       label: 'Döviz İşlemi' },
  { key: 'iade',     label: 'İade' },
  { key: 'satis',    label: 'Satış Gelirleri' },
];

function SearchableSelect({ options, value, onChange, placeholder, className = '' }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);
  const inputRef = useRef(null);

  const selected = options.find(o => o.id === value);
  const filtered = useMemo(() =>
    options.filter(o => trNorm(o.name).includes(trNorm(query))).slice(0, 60),
    [options, query]
  );

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div ref={ref} className={`relative ${className}`}>
      <input
        ref={inputRef}
        type="text"
        value={open ? query : (selected?.name || '')}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => { setQuery(''); setOpen(true); }}
        placeholder={placeholder || 'Seç veya ara...'}
        className="w-full h-8 px-2.5 rounded-md border border-[#d0c4b0] bg-white text-[11px] focus:outline-none focus:border-[#3d6e4e] focus:ring-1 focus:ring-[#3d6e4e]/30"
      />
      {open && (
        <div className="absolute z-[200] top-full left-0 right-0 mt-0.5 bg-white border border-[#d0c4b0] rounded-md shadow-lg max-h-52 overflow-y-auto">
          <div
            onMouseDown={() => { onChange(''); setQuery(''); setOpen(false); }}
            className="px-3 py-1.5 text-[11px] text-[#8a7e70] cursor-pointer hover:bg-[#f5f0e8] border-b border-[#e8dfd0]">
            — Boş bırak —
          </div>
          {filtered.length === 0 ? (
            <div className="px-3 py-2 text-[10px] text-[#8a7e70]">Sonuç bulunamadı</div>
          ) : filtered.map(o => (
            <div key={o.id}
              onMouseDown={() => { onChange(o.id); setQuery(''); setOpen(false); }}
              className={`px-3 py-1.5 text-[11px] cursor-pointer hover:bg-[#f5f0e8] ${o.id === value ? 'bg-[#e8dfd0] font-semibold' : ''}`}>
              {o.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function NewTransactionModal({
  open, onClose, accounts, suppliers,
  defaultAccountId, defaultCurrency, defaultSupplierId, onSaved
}) {
  const today = new Date().toISOString().split('T')[0];

  const [txType, setTxType] = useState(defaultSupplierId ? 'cari' : 'transfer');
  const [saving, setSaving] = useState(false);

  const [date, setDate] = useState(today);
  const [description, setDescription] = useState('');

  const [debitId, setDebitId]   = useState(defaultAccountId || '');
  const [creditId, setCreditId] = useState(defaultSupplierId || '');
  const [amount, setAmount]     = useState('');
  const [currency, setCurrency] = useState(defaultCurrency || 'TRY');

  const [fxOutId, setFxOutId]         = useState('');
  const [fxOutAmount, setFxOutAmount] = useState('');
  const [fxOutCurr, setFxOutCurr]     = useState('USD');
  const [fxInId, setFxInId]           = useState(defaultAccountId || '');
  const [fxInAmount, setFxInAmount]   = useState('');
  const [fxInCurr, setFxInCurr]       = useState('TRY');
  const [descSuffix, setDescSuffix]   = useState('');

  const autoRate = (() => {
    const out = parseFloat(String(fxOutAmount).replace(',', '.'));
    const inn = parseFloat(String(fxInAmount).replace(',', '.'));
    if (!out || !inn || fxOutCurr === fxInCurr) return null;
    return inn / out;
  })();

  const autoRateText = autoRate != null ? `1 ${fxOutCurr} = ${fmtTR(autoRate, 4)} ${fxInCurr}` : '';
  const fullDescription = txType === 'fx' ? [autoRateText, descSuffix].filter(Boolean).join(' | ') : description;

  // Hesaplar da cariler de aynı koleksiyonda (suppliers) tutuluyor; `/suppliers`
  // kasa/banka hesaplarını da döndürdüğü için ikisini üst üste eklersek aynı
  // hesap hem "Hesap: X" hem "Cari: X" olarak iki kez listelenir. Kasa/banka
  // id'lerini ayıklayarak tek liste kuruyoruz.
  const accountIds = new Set((accounts || []).map(a => a.id));
  const allTargets = [
    ...(accounts || []).map(a => ({ id: a.id, name: `Hesap: ${a.name}` })),
    ...(suppliers || []).filter(s => !accountIds.has(s.id))
      .map(s => ({ id: s.id, name: `Cari: ${s.name}` })),
  ];

  const reset = () => {
    setTxType(defaultSupplierId ? 'cari' : 'transfer');
    setDate(today); setDescription('');
    setDebitId(defaultAccountId || ''); setCreditId(defaultSupplierId || '');
    setAmount(''); setCurrency(defaultCurrency || 'TRY');
    setFxOutId(''); setFxOutAmount(''); setFxOutCurr('USD');
    setFxInId(defaultAccountId || ''); setFxInAmount(''); setFxInCurr('TRY'); setDescSuffix('');
    setYeniEkler([]);
  };

  // Harekete eklenecek dosyalar (banka dekontu, makbuz) — kayıttan SONRA yüklenir
  const [yeniEkler, setYeniEkler] = useState([]);

  // Kaydedilen hareketin dekontu — pencere kapansa da açık kalır
  const [dekontlar, setDekontlar] = useState(null);

  const handleClose = () => { reset(); onClose(); };

  const save = async () => {
    if (!date) { toast.error('Tarih zorunlu'); return; }
    setSaving(true);
    const yeniDekontlar = [];
    try {
      if (txType === 'fx') {
        if (!fxOutId && !fxInId) { toast.error('En az bir hesap seçin'); setSaving(false); return; }
        if (!fxOutAmount || !fxInAmount) { toast.error('Her iki tutarı da girin'); setSaving(false); return; }
        const desc = fullDescription;
        const cikan = await api.post('/treasury/transactions', {
          transaction_date: date, debit_account_id: '', credit_account_id: fxOutId,
          amount: parseFloat(String(fxOutAmount).replace(',', '.')), currency: fxOutCurr, description: desc, exchange_rate: 1,
        });
        const giren = await api.post('/treasury/transactions', {
          transaction_date: date, debit_account_id: fxInId, credit_account_id: '',
          amount: parseFloat(String(fxInAmount).replace(',', '.')), currency: fxInCurr, description: desc, exchange_rate: 1,
        });
        toast.success('2 hareket kaydedildi');
        // Döviz işleminin her bacağının kendi dekontu var
        yeniDekontlar.push(...[[cikan, `Çıkan ${fxOutCurr}`], [giren, `Giren ${fxInCurr}`]]
          .filter(([r]) => r.data?.id).map(([r, etiket]) => ({ id: r.data.id, etiket })));
      } else {
        if (!amount) { toast.error('Tutar zorunlu'); setSaving(false); return; }
        if (!debitId && !creditId) { toast.error('En az bir hesap seçin'); setSaving(false); return; }
        const r = await api.post('/treasury/transactions', {
          transaction_date: date, debit_account_id: debitId, credit_account_id: creditId,
          amount: parseFloat(String(amount).replace(',', '.')), currency, description, exchange_rate: 1,
        });
        toast.success('Hareket kaydedildi');
        if (r.data?.id) yeniDekontlar.push({ id: r.data.id, etiket: 'Bu hareket' });
      }
      await kayittanSonraYukle(yeniDekontlar.map(d => d.id), yeniEkler);
      onSaved();
      handleClose();
      if (yeniDekontlar.length) setDekontlar(yeniDekontlar);
    } catch {
      toast.error('Kayıt hatası');
    }
    setSaving(false);
  };

  // Dekont penceresi, hareket modalı kapandıktan SONRA gösteriliyor
  if (!open) return <DekontDialog dekontlar={dekontlar} onClose={() => setDekontlar(null)} />;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" onClick={handleClose} />
      <div className="relative bg-[#faf7f2] border border-[#d0c4b0] rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-[#e8dfd0] bg-[#f5f0e8]">
          <h2 className="text-sm font-bold text-[#1a1916]">Yeni Hareket</h2>
          <button onClick={handleClose} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-[#e8dfd0] text-[#8a7e70]">
            <X size={14} />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div>
            <label className="block text-[10px] font-semibold text-[#8a7e70] uppercase tracking-wide mb-1.5">İşlem Türü</label>
            <div className="flex gap-1.5">
              {TX_TYPES.map(t => (
                <button key={t.key} onClick={() => setTxType(t.key)}
                  className={`flex-1 py-1.5 px-2 rounded-lg text-[10px] font-semibold border transition-colors ${
                    txType === t.key ? 'bg-[#2e2d29] text-[#f5f0e8] border-[#2e2d29]' : 'bg-white text-[#5a5650] border-[#d0c4b0] hover:border-[#8a7e70]'
                  }`}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-[10px] font-semibold text-[#8a7e70] uppercase tracking-wide mb-1.5">Tarih</label>
            <Input type="date" value={date} onChange={e => setDate(e.target.value)}
              className="h-8 text-[11px] bg-white border-[#d0c4b0] w-40" />
          </div>

          {txType === 'fx' && (
            <div className="space-y-3">
              <div className="bg-[#fff0f0]/60 border border-[#f0d0d0] rounded-xl p-3 space-y-2">
                <div className="text-[10px] font-bold text-[#7a3535] uppercase tracking-wide">Çıkan (Para Ayrılan Hesap)</div>
                <SearchableSelect options={allTargets} value={fxOutId} onChange={setFxOutId} placeholder="Hesap veya cari seç..." />
                <div className="flex gap-2">
                  <Input type="number" step="0.01" value={fxOutAmount} onChange={e => setFxOutAmount(e.target.value)}
                    placeholder="Tutar" className="h-8 text-[11px] bg-white border-[#d0c4b0] flex-1 text-right font-mono" />
                  <select value={fxOutCurr} onChange={e => setFxOutCurr(e.target.value)}
                    className="h-8 px-2 rounded-md border border-[#d0c4b0] bg-white text-[11px] focus:outline-none w-20">
                    {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              {autoRateText && (
                <div className="text-center text-[10px] font-mono text-[#5a5650] bg-[#e8dfd0] rounded-lg py-1.5 px-3">{autoRateText}</div>
              )}
              <div className="bg-[#f0fff4]/60 border border-[#c8e6d0] rounded-xl p-3 space-y-2">
                <div className="text-[10px] font-bold text-[#3d6e4e] uppercase tracking-wide">Giren (Para Alınan Hesap)</div>
                <SearchableSelect options={allTargets} value={fxInId} onChange={setFxInId} placeholder="Hesap veya cari seç..." />
                <div className="flex gap-2">
                  <Input type="number" step="0.01" value={fxInAmount} onChange={e => setFxInAmount(e.target.value)}
                    placeholder="Tutar" className="h-8 text-[11px] bg-white border-[#d0c4b0] flex-1 text-right font-mono" />
                  <select value={fxInCurr} onChange={e => setFxInCurr(e.target.value)}
                    className="h-8 px-2 rounded-md border border-[#d0c4b0] bg-white text-[11px] focus:outline-none w-20">
                    {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-[#8a7e70] uppercase tracking-wide mb-1.5">
                  Açıklama {autoRateText && <span className="font-normal normal-case text-[#3d6e4e]">({autoRateText} otomatik eklendi)</span>}
                </label>
                <Input value={descSuffix} onChange={e => setDescSuffix(e.target.value)}
                  placeholder="Ek açıklama (isteğe bağlı)..." className="h-8 text-[11px] bg-white border-[#d0c4b0]" />
              </div>
            </div>
          )}

          {(txType === 'transfer' || txType === 'cari' || txType === 'iade' || txType === 'satis') && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] font-semibold text-[#3d6e4e] uppercase tracking-wide mb-1.5">Giren Hesap</label>
                  <SearchableSelect
                    options={allTargets}
                    value={debitId} onChange={setDebitId} placeholder="Seç veya ara..." />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-[#7a3535] uppercase tracking-wide mb-1.5">Çıkan Hesap</label>
                  <SearchableSelect
                    options={allTargets}
                    value={creditId} onChange={setCreditId} placeholder="Seç veya ara..." />
                </div>
              </div>
              <div className="flex gap-2 items-end">
                <div className="flex-1">
                  <label className="block text-[10px] font-semibold text-[#8a7e70] uppercase tracking-wide mb-1.5">Tutar</label>
                  <Input type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)}
                    placeholder="0,00" className="h-8 text-[11px] bg-white border-[#d0c4b0] text-right font-mono"
                    onKeyDown={e => { if (e.key === 'Enter') save(); }} />
                </div>
                <div>
                  <label className="block text-[10px] font-semibold text-[#8a7e70] uppercase tracking-wide mb-1.5">Para Birimi</label>
                  <select value={currency} onChange={e => setCurrency(e.target.value)}
                    className="h-8 px-2 rounded-md border border-[#d0c4b0] bg-white text-[11px] focus:outline-none w-20">
                    {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-[10px] font-semibold text-[#8a7e70] uppercase tracking-wide mb-1.5">Açıklama</label>
                <Input value={description} onChange={e => setDescription(e.target.value)}
                  placeholder="Açıklama..." className="h-8 text-[11px] bg-white border-[#d0c4b0]"
                  onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') handleClose(); }} />
              </div>
            </div>
          )}
          <EkSecici dosyalar={yeniEkler} setDosyalar={setYeniEkler} />
        </div>

        <div className="px-5 py-3 border-t border-[#e8dfd0] bg-[#f5f0e8] flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={handleClose} className="h-8 text-[11px] border-[#d0c4b0] text-[#5a5650]">İptal</Button>
          <Button size="sm" onClick={save} disabled={saving} className="h-8 text-[11px] bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916] min-w-[80px]">
            {saving ? <Loader2 size={12} className="animate-spin" /> : 'Kaydet'}
          </Button>
        </div>
      </div>
    </div>
  );
}
